import { useMemo } from 'react'
import { compose, hoc } from '@'
import { useForm, useFormLoading, useFormValues } from '@form'
import { withModalV4, useModal } from '@modal'
import { check } from 'components/can'
import CreateRegionMapping from '../../actions/CreateRegionMapping'
import UpdateRegion from '../../actions/UpdateRegion'
import { MAX_IDS, RESOURCE, parseIds, text } from '../CoverageCommon'

const NAME = 'RegionCoverageHotelsForm'

const $fieldProps = {
    region_id: { rules: [{ required: true, whitespace: true, message: 'Please enter a region_id' }] },
    supplier_code: { rules: [{ required: true }] },
    hotel_ids: {
        dependencies: ['copy_from_region_id'],
        rules: [
            ({ getFieldValue }) => ({
                validator: (_, value) => {
                    const ids = parseIds(value)
                    if (!ids.length && !text(getFieldValue('copy_from_region_id'))) {
                        return Promise.reject(new Error('Enter hotel ids or a region to copy from'))
                    }
                    if (ids.length > MAX_IDS) {
                        return Promise.reject(new Error(`At most ${MAX_IDS} hotel ids per request`))
                    }
                    return Promise.resolve()
                }
            })
        ]
    },
    copy_from_region_id: {
        dependencies: ['region_id'],
        rules: [
            ({ getFieldValue }) => ({
                validator: (_, value) =>
                    text(value) && text(value) === text(getFieldValue('region_id'))
                        ? Promise.reject(new Error('Pick a different region to copy from'))
                        : Promise.resolve()
            })
        ]
    }
}

const container = compose(
    hoc(props => {
        const { $modal } = props

        const $values = useFormValues(NAME)

        const [$props, $form] = useForm(() => {
            const onSubmit = async values => {
                const regionId = text(values.region_id)
                const hotelIds = parseIds(values.hotel_ids)
                const copyFrom = text(values.copy_from_region_id)
                // Adding to a region that already has rows only needs `edit`; creating one or copying needs `create`.
                if (values.mode === 'add' && !copyFrom) {
                    await UpdateRegion(regionId, { supplier_code: values.supplier_code, add_hotel_ids: hotelIds })
                } else {
                    const body = { region_id: regionId, supplier_code: values.supplier_code }
                    if (hotelIds.length) {
                        body.hotel_ids = hotelIds
                    }
                    if (copyFrom) {
                        body.copy_from_region_id = copyFrom
                    }
                    await CreateRegionMapping(body)
                }
                $modal.close()
            }

            return {
                name: NAME,
                onSubmit,
                props: { colon: false, layout: 'vertical', wrapperCol: { flex: 1 } }
            }
        }, [$modal])

        const $loading = useFormLoading($form.name)

        $modal.setForm($form)
        $modal.setLoading($loading)

        const idCount = useMemo(() => parseIds($values && $values.hotel_ids).length, [$values])

        return {
            idCount,
            lockRegion: !!($values && $values.lock_region),
            canCopy: check(RESOURCE, 'create'),
            $form,
            $props,
            $loading,
            $fieldProps,
            ...props
        }
    }),
    withModalV4(props => {
        const $values = useFormValues(NAME)

        const mode = $values && $values.mode
        const lockedRegion = $values && $values.lock_region ? $values.region_id : null

        const [$props, $modal] = useModal(() => {
            let title = 'New region mapping'
            if (lockedRegion) {
                title = mode === 'add' ? `Add hotels to region ${lockedRegion}` : `Map hotels to region ${lockedRegion}`
            }
            return {
                name: NAME,
                props: {
                    title,
                    width: 720,
                    destroyOnClose: true,
                    okText: mode === 'add' ? 'Add' : 'Create'
                }
            }
        }, [mode, lockedRegion])

        return {
            $modal,
            $props,
            ...props
        }
    })
)

export default container
